"use client";

import { Shield, Crown, Gem, Medal, Award, Star, CircleDot } from "lucide-react";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import {
  getCurrentTier,
  getNextTier,
  getTierProgress,
  type RewardTier,
} from "../types/rewards.types";

const TIER_ICONS: Record<string, React.ElementType> = {
  Unranked: CircleDot,
  Bronze: Medal,
  Silver: Award,
  Gold: Star,
  Platinum: Shield,
  Diamond: Gem,
  Elite: Crown,
};

interface RewardsTierCardProps {
  totalConversions: number;
}

export default function RewardsTierCard({ totalConversions }: RewardsTierCardProps) {
  const currentTier = getCurrentTier(totalConversions);
  const nextTier = getNextTier(totalConversions);
  const progress = getTierProgress(totalConversions);
  const TierIcon = TIER_ICONS[currentTier.name] || CircleDot;

  return (
    <Card className={`${currentTier.borderColor} border-2`}>
      <CardContent className="pt-6">
        <div className="flex items-center gap-6">
          <div className={`w-16 h-16 rounded-full ${currentTier.iconBg} flex items-center justify-center flex-shrink-0`}>
            <TierIcon className={`h-8 w-8 ${currentTier.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className={`text-2xl font-bold ${currentTier.color}`}>
                {currentTier.name}
              </h3>
              <span className="text-sm text-gray-500">
                {totalConversions} conversion{totalConversions !== 1 ? "s" : ""}
              </span>
            </div>
            {nextTier ? (
              <>
                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                  <div
                    className="h-2.5 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600">
                  {nextTier.minConversions - totalConversions} more conversion
                  {nextTier.minConversions - totalConversions !== 1 ? "s" : ""} to reach{" "}
                  <span className={`font-semibold ${nextTier.color}`}>
                    {nextTier.name}
                  </span>
                </p>
              </>
            ) : (
              <p className="text-sm text-purple-600 font-medium">
                You've reached the highest tier!
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
